import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdRefresh } from 'react-icons/md';
import DefaultButton from '../../ReusableComponents/DefaultButton';
import { usePortalQuery, usePortalMutation } from '../hooks';
import FormBuilder from './FormBuilder';
import RequirementForm from './RequirementForm';
import FormDocuments from './FormDocuments';
import PortalFormModal from './PortalFormModal';
import { Loading, ErrorNotice } from './Feedback';

function FormEditor({ selection, version, onClose, onSaved }) {
  const [fields, setFields] = useState(version.fields);
  const [document, setDocument] = useState(null);
  const bodyRef = useRef(null);
  const scrollTop = useRef(0);
  const restoreBodyScroll = useCallback((node) => {
    bodyRef.current = node;
    if (node) node.scrollTop = scrollTop.current;
  }, []);
  const openDocument = (value) => {
    scrollTop.current = bodyRef.current?.scrollTop || 0;
    setDocument(value);
  };
  const [documents, setDocuments] = useState(selection.document_requirements || []);
  useEffect(() => setDocuments(selection.document_requirements || []), [selection.document_requirements]);
  const save = usePortalMutation(`selecoes/${selection.id}/formulario`);
  if (document) return (
    <RequirementForm
      selection={selection}
      item={document.item}
      defaults={document.defaults}
      onDone={(saved) => {
        if (saved) setDocuments(all => [...all.filter(item => item.id !== saved.id), saved]);
        if (saved && document.index !== undefined) {
          setFields(all => all.filter((_, index) => index !== document.index));
        }
        setDocument(null);
      }}
    />
  );
  return (
    <PortalFormModal
      title="Campos do formulário de inscrição"
      bodyRef={restoreBodyScroll}
      description={`Versão ${version.version} · ${selection.title}`}
      onClose={onClose}
      busy={save.isLoading}
      allowed={save.allowed}
      error={save.error}
      onSubmit={() => {
        if (JSON.stringify(fields) === JSON.stringify(version.fields)) {
          onClose();
          return;
        }
        save.mutate(
          { fields },
          {
            onSuccess: () => {
              onSaved({});
              onClose();
            },
          }
        );
      }}
    >
      <p className="form-text">
        Inscrições já enviadas preservam a versão utilizada na confirmação.
      </p>
      <FormBuilder fields={fields} setFields={setFields} onDocument={(index, field) => openDocument({
        index,
        defaults: { title: field.label, instructions: field.description, required: field.required,
          max_kb: field.max_kb, allowed_types: field.file_types === 'pdf' ? ['pdf'] : field.file_types === 'image' ? ['jpg', 'jpeg', 'png'] : ['pdf', 'jpg', 'jpeg', 'png'] },
      })} />
      <FormDocuments documents={documents} allowed={save.allowed} onAdd={() => openDocument({})} onEdit={item => openDocument({ item })} />
    </PortalFormModal>
  );
}
export default function FormConfigurationModal({ selection, onClose, onSaved }) {
  const query = usePortalQuery(`selecoes/${selection.id}/formulario`);
  const initial = useRef(null);
  if (!initial.current && query.data?.data) initial.current = query.data.data;
  if (initial.current)
    return (
      <FormEditor
        selection={selection}
        version={initial.current}
        onClose={onClose}
        onSaved={onSaved}
      />
    );
  return (
    <PortalFormModal
      title="Campos do formulário de inscrição"
      onClose={onClose}
      submitText={null}
      requiredNote={false}
      onSubmit={() => {}}
    >
      {query.isLoading ? (
        <Loading />
      ) : (
        <>
          <ErrorNotice
            error={query.error || new Error('Não foi possível carregar o formulário.')}
          />
          <DefaultButton
            text="Tentar novamente"
            bg="primary"
            icon={<MdRefresh />}
            onClick={() => query.refetch()}
          />
        </>
      )}
    </PortalFormModal>
  );
}
